import { useCallback, useEffect, useRef, useState } from 'react';

export function useDelayedHover({ enterDelay = 200, leaveDelay = 200 } = {}) {
  const [isHovered, setIsHovered] = useState(false);
  const enterTimeoutRef = useRef(null);
  const leaveTimeoutRef = useRef(null);

  const clearEnterTimeout = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
  }, []);

  const clearLeaveTimeout = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const clearAllTimeouts = useCallback(() => {
    clearEnterTimeout();
    clearLeaveTimeout();
  }, [clearEnterTimeout, clearLeaveTimeout]);

  const onMouseEnter = useCallback(() => {
    clearLeaveTimeout();
    if (isHovered) return;

    clearEnterTimeout();
    enterTimeoutRef.current = setTimeout(() => {
      setIsHovered(true);
      enterTimeoutRef.current = null;
    }, enterDelay);
  }, [clearEnterTimeout, clearLeaveTimeout, enterDelay, isHovered]);

  const onMouseLeave = useCallback(() => {
    clearEnterTimeout();
    if (!isHovered) return;

    clearLeaveTimeout();
    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      leaveTimeoutRef.current = null;
    }, leaveDelay);
  }, [clearEnterTimeout, clearLeaveTimeout, isHovered, leaveDelay]);

  const setHoveredImmediate = useCallback((next) => {
    clearAllTimeouts();
    setIsHovered(Boolean(next));
  }, [clearAllTimeouts]);

  useEffect(() => () => {
    clearAllTimeouts();
  }, [clearAllTimeouts]);

  return {
    isHovered,
    onMouseEnter,
    onMouseLeave,
    setHoveredImmediate,
    clearHoverTimeouts: clearAllTimeouts,
  };
}

export default useDelayedHover;